/**
 * BreakdownBar.tsx — Hand-rolled segmented source-breakdown bar (D-07-06).
 *
 * Renders a horizontal segmented bar + legend for the 4-way transaction kind breakdown
 * (subscription / ppv_unlock / tip / other), with the required D-07-02 disclaimer.
 *
 * Implementation notes:
 *   - No charting library (D-07-06: hand-rolled CSS flex bar only — no recharts/chart/d3/victory/nivo).
 *   - Uses inline styles with CSS variables only (sidesteps the @source Tailwind gotcha; RESEARCH Pitfall 6).
 *   - KIND_COLORS maps to the admin theme tokens (green accent, warning amber, success green, muted gray).
 */

import * as React from 'react';
import type { KindBreakdownEntry } from '../../lib/api-client.js';

// ---------------------------------------------------------------------------
// Color mapping — CSS variable names from the admin theme (D-07-06, D-01)
// ---------------------------------------------------------------------------

const KIND_COLORS: Record<string, string> = {
  subscription: 'var(--color-accent)',      // green #15a06a
  ppv_unlock:   'var(--color-warning)',     // amber
  tip:          'var(--color-success)',     // success green (lighter shade)
  other:        'var(--color-text-muted)', // muted gray
};

/** Human-readable labels for each kind. */
const KIND_LABELS: Record<string, string> = {
  subscription: 'Subscription',
  ppv_unlock:   'PPV unlock',
  tip:          'Tip',
  other:        'Other',
};

// ---------------------------------------------------------------------------
// BreakdownBar component
// ---------------------------------------------------------------------------

/**
 * BreakdownBar — Segmented horizontal bar showing the 4-way earnings source breakdown.
 *
 * Props:
 *   entries — flat KindBreakdownEntry[] (from GET /kpi/breakdown, pre-aggregated across creators)
 *
 * Required by D-07-02: must carry the "estimated — unconfirmed until live capture" disclaimer.
 */
export function BreakdownBar({
  entries,
}: {
  entries: KindBreakdownEntry[];
}) {
  const total = entries.reduce((s, e) => s + e.net_minor, 0);

  if (total === 0) {
    return (
      <div
        style={{
          color: 'var(--color-text-muted)',
          fontSize: '12px',
          padding: '8px 0',
        }}
        role="img"
        aria-label="No earnings data available for this period"
      >
        No data
      </div>
    );
  }

  return (
    <div role="img" aria-label="Earnings source breakdown bar">
      {/* Segmented bar */}
      <div
        style={{
          display: 'flex',
          height: '12px',
          borderRadius: '6px',
          overflow: 'hidden',
          gap: '1px',
          background: 'var(--color-border)',
        }}
      >
        {entries.map((e) => {
          const widthPct = (e.net_minor / total) * 100;
          if (widthPct === 0) return null;
          return (
            <div
              key={e.kind}
              style={{
                width: `${widthPct}%`,
                background: KIND_COLORS[e.kind] ?? KIND_COLORS.other,
                flexShrink: 0,
              }}
              title={`${KIND_LABELS[e.kind] ?? e.kind}: €${(e.net_minor / 100).toFixed(2)} (${widthPct.toFixed(1)}%)`}
              aria-label={`${KIND_LABELS[e.kind] ?? e.kind}: ${widthPct.toFixed(1)}%`}
            />
          );
        })}
      </div>

      {/* Legend */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          marginTop: '6px',
          fontSize: '11px',
          color: 'var(--color-text)',
        }}
      >
        {entries.map((e) => {
          const pct = ((e.net_minor / total) * 100).toFixed(0);
          return (
            <span
              key={e.kind}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: KIND_COLORS[e.kind] ?? KIND_COLORS.other,
                  flexShrink: 0,
                  display: 'inline-block',
                }}
              />
              {KIND_LABELS[e.kind] ?? e.kind} ({pct}%)
            </span>
          );
        })}
      </div>

      {/* Required D-07-02 disclaimer — PPV/tip categories are estimated until live capture */}
      <p
        style={{
          fontSize: '11px',
          color: 'var(--color-text-muted)',
          marginTop: '4px',
          marginBottom: 0,
        }}
      >
        * PPV/tip categories estimated — unconfirmed until live capture
      </p>
    </div>
  );
}
